import React, { useState, useEffect } from 'react';
import { t } from '../lib/i18n';
import { getLicense, getEmailConfig, listUsers, inviteUser, exportData, myUsage } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Shield, Users, Mail, Download, Key, Plus, Crown,
  MessageSquare, FileText, Clock, LogOut, GraduationCap, RotateCcw, Loader2,
  Bot, Phone, Mic, Settings as SettingsIcon, ArrowRight, Send, Unlink,
} from 'lucide-react';
import {
  getTelegramStatus, generateTelegramPairingLink, unlinkTelegram, testTelegramNotify,
} from '../lib/api';
import { restartTutorial } from '../lib/tutorialActions';
import { Link } from 'react-router-dom';
import { useAgentConfigGate } from '../components/common/JITAgentConfigModal';

export default function Settings({ lang, tenantId, user, onLogout }) {
  const [license, setLicense] = useState(null);
  const [emailConfig, setEmailConfig] = useState(null);
  const [users, setUsers] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', password: '', name: '', company: '' });
  const [restartingTutorial, setRestartingTutorial] = useState(false);

  // T13 2026-05-27 — JIT config gates dla karty "Konfiguracja agenta".
  // 3 oddzielne hooki — każdy własny modal + config state. Pozwala userom
  // świadomie konfigurować email/phone/voice **gdy chcą**, bez forced wizard.
  const emailGate = useAgentConfigGate('email');
  const phoneGate = useAgentConfigGate('phone');
  const voiceGate = useAgentConfigGate('voice');

  // T32 2026-05-29 — Telegram bot pairing state
  const [tgStatus, setTgStatus] = useState(null);
  const [tgBusy, setTgBusy] = useState(false);

  useEffect(() => {
    getTelegramStatus().then(setTgStatus).catch(() => setTgStatus(null));
  }, []);

  async function handleTgPair() {
    setTgBusy(true);
    try {
      const r = await generateTelegramPairingLink();
      if (r.pairing_link) {
        window.open(r.pairing_link, '_blank');
        toast.info(`Link otwarty w nowej karcie. Kliknij START w Telegramie. Link wygasa za ${r.expires_in_minutes} min.`);
        // Re-check za 15s żeby sprawdzić czy zsparowane
        setTimeout(() => getTelegramStatus().then(setTgStatus).catch(() => {}), 15000);
      }
    } catch (e) {
      toast.error('Generowanie linku nie powiodło się: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTgBusy(false);
    }
  }
  async function handleTgUnlink() {
    if (!window.confirm('Odłączyć Telegram od konta?')) return;
    setTgBusy(true);
    try {
      await unlinkTelegram();
      toast.success('Odłączono');
      getTelegramStatus().then(setTgStatus);
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTgBusy(false);
    }
  }
  async function handleTgTest() {
    setTgBusy(true);
    try {
      const r = await testTelegramNotify('Test z Settings', 'Notifications działają ✓');
      toast.success(r.sent ? 'Wysłano! Sprawdź Telegram.' : `Nie wysłano: ${JSON.stringify(r.telegram_response)}`);
    } catch (e) {
      toast.error('Test failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTgBusy(false);
    }
  }

  useEffect(() => { loadData(); }, [tenantId]);

  async function loadData() {
    setLoading(true);
    try {
      const [lic, email, usrs, usg] = await Promise.all([
        getLicense().catch(() => null),
        getEmailConfig().catch(() => null),
        listUsers().catch(() => []),
        myUsage().catch(() => null),
      ]);
      setLicense(lic);
      setEmailConfig(email);
      setUsers(usrs);
      setUsage(usg);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleInvite() {
    if (!inviteForm.email || !inviteForm.password || !inviteForm.name) {
      toast.error('Wszystkie pola s\u0105 wymagane');
      return;
    }
    try {
      await inviteUser(inviteForm);
      toast.success('U\u017cytkownik zaproszony');
      setInviteOpen(false);
      setInviteForm({ email: '', password: '', name: '', company: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'B\u0142\u0105d');
    }
  }

  async function handleRestartTutorial() {
    if (!window.confirm(lang === 'pl'
      ? 'Zresetować postęp samouczka? Welcome modal pojawi się ponownie, a Mission Board wyzeruje wszystkie ukończone misje.'
      : 'Reset tutorial progress? The Welcome modal will reappear and Mission Board will reset all completed missions.')) {
      return;
    }
    setRestartingTutorial(true);
    try {
      const r = await restartTutorial();
      if (r.ok) {
        toast.success(lang === 'pl' ? 'Samouczek zresetowany — odśwież stronę' : 'Tutorial reset — refresh page');
        // Soft reload after 1s so user sees toast
        setTimeout(() => window.location.reload(), 1200);
      } else {
        toast.error(r.error || (lang === 'pl' ? 'Błąd resetu' : 'Reset error'));
      }
    } finally {
      setRestartingTutorial(false);
    }
  }

  async function handleExport(type, format) {
    try {
      const data = await exportData(type, format, tenantId);
      if (format === 'csv') {
        const blob = new Blob([data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_export.csv`;
        a.click();
      } else {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_export.json`;
        a.click();
      }
      toast.success('Eksport gotowy');
    } catch (err) {
      toast.error('B\u0142\u0105d eksportu');
    }
  }

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 w-full" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {t('settings', lang)}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {user?.email} \u2022 {user?.role === 'admin' ? 'Administrator' : 'U\u017cytkownik'}
          </p>
        </div>
        <Button variant="ghost" onClick={onLogout} className="gap-2 text-muted-foreground hover:text-destructive">
          <LogOut className="w-4 h-4" />
          {lang === 'pl' ? 'Wyloguj' : 'Logout'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* License */}
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Crown className="w-4 h-4 text-primary" />
              {lang === 'pl' ? 'Licencja' : 'License'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {license ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Plan</span>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                    {license.plan?.toUpperCase()}
                  </Badge>
                </div>
                {license.expires_at && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {lang === 'pl' ? 'Wa\u017cna do' : 'Expires'}
                    </span>
                    <span>{new Date(license.expires_at).toLocaleDateString('pl-PL')}</span>
                  </div>
                )}
                <Separator />
                <div className="space-y-3">
                  {[{key: 'negotiations', icon: MessageSquare, label: lang === 'pl' ? 'Negocjacje' : 'Negotiations'},
                    {key: 'contractors', icon: Users, label: lang === 'pl' ? 'Kontrahenci' : 'Contractors'},
                    {key: 'users', icon: Shield, label: lang === 'pl' ? 'U\u017cytkownicy' : 'Users'}].map(({key, icon: Icon, label}) => (
                    <div key={key}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Icon className="w-3 h-3" /> {label}
                        </span>
                        <span>{license.usage?.[key] || 0} / {license.limits?.[`max_${key}`] || '\u221e'}</span>
                      </div>
                      <Progress value={((license.usage?.[key] || 0) / (license.limits?.[`max_${key}`] || 100)) * 100} className="h-1.5" />
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(license.features || []).map((f, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{lang === 'pl' ? 'Brak licencji' : 'No license'}</p>
            )}
          </CardContent>
        </Card>

        {/* My LLM Usage */}
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              {lang === 'pl' ? 'Moje zużycie AI' : 'My AI usage'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {usage && usage.tokens_limit !== undefined ? (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Tokens (miesiąc)</span>
                  <span className="font-medium">
                    {Number(usage.tokens_used || 0).toLocaleString('pl-PL')} /{' '}
                    {usage.tokens_limit === -1 ? '∞' : Number(usage.tokens_limit).toLocaleString('pl-PL')}
                  </span>
                </div>
                {usage.tokens_limit !== -1 && usage.tokens_limit > 0 && (
                  <Progress
                    value={usage.usage_pct}
                    className={`h-2 ${
                      usage.usage_pct >= 90 ? '[&>div]:bg-red-500' :
                      usage.usage_pct >= 70 ? '[&>div]:bg-amber-500' : ''
                    }`}
                  />
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{lang === 'pl' ? 'Szacowany koszt' : 'Estimated cost'}</span>
                  <span className="font-medium">
                    {Number(usage.cost_pln_estimate || 0).toLocaleString('pl-PL', { maximumFractionDigits: 2 })} PLN
                    {' '}<span className="text-muted-foreground text-xs">(${usage.cost_usd_estimate})</span>
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{lang === 'pl' ? 'Status konta' : 'Account status'}</span>
                  <Badge variant={usage.account_status === 'active' ? 'outline' : 'destructive'}
                         className={usage.account_status === 'active' ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' : ''}>
                    {usage.account_status === 'suspended' ? (lang === 'pl' ? 'zawieszony' : 'suspended') :
                     usage.account_status === 'deactivated' ? (lang === 'pl' ? 'dezaktywowany' : 'deactivated') :
                     'aktywny'}
                  </Badge>
                </div>
                {usage.suspension_reason && (
                  <div className="text-xs text-amber-300 bg-amber-500/10 p-2 rounded">
                    ⚠ {lang === 'pl' ? 'Powód zawieszenia:' : 'Suspension reason:'} {usage.suspension_reason}
                  </div>
                )}
                {usage.usage_pct >= 80 && usage.tokens_limit !== -1 && (
                  <div className="text-xs text-amber-300 bg-amber-500/10 p-2 rounded">
                    ⚠ {lang === 'pl'
                      ? `Wykorzystałeś już ${usage.usage_pct}% miesięcznego limitu. Po przekroczeniu konto zostanie zawieszone do następnego okresu rozliczeniowego.`
                      : `You've used ${usage.usage_pct}% of monthly limit. Exceeding will suspend account until next billing period.`}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{lang === 'pl' ? 'Brak danych o zużyciu' : 'No usage data'}</p>
            )}
          </CardContent>
        </Card>

        {/* Users */}
        <Card className="border border-border">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              {lang === 'pl' ? 'U\u017cytkownicy' : 'Users'} ({users.length})
            </CardTitle>
            {user?.role === 'admin' && (
              <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1">
                    <Plus className="w-3.5 h-3.5" />
                    {lang === 'pl' ? 'Zapro\u015b' : 'Invite'}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{lang === 'pl' ? 'Zapro\u015b u\u017cytkownika' : 'Invite User'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div>
                      <Label className="text-xs">{lang === 'pl' ? 'Imi\u0119' : 'Name'} *</Label>
                      <Input value={inviteForm.name} onChange={e => setInviteForm(p => ({...p, name: e.target.value}))} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Email *</Label>
                      <Input value={inviteForm.email} onChange={e => setInviteForm(p => ({...p, email: e.target.value}))} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">{lang === 'pl' ? 'Has\u0142o tymczasowe' : 'Temp password'} *</Label>
                      <Input type="password" value={inviteForm.password} onChange={e => setInviteForm(p => ({...p, password: e.target.value}))} className="mt-1" />
                    </div>
                    <Button onClick={handleInvite} className="w-full">{lang === 'pl' ? 'Zapro\u015b' : 'Invite'}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {users.map(u => (
                <div key={u.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                  <div>
                    <p className="text-sm font-medium">{u.name}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">{u.role}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Email Config */}
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" />
              Email
            </CardTitle>
          </CardHeader>
          <CardContent>
            {emailConfig ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Provider</span>
                  <span>{emailConfig.provider}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">SMTP host</span>
                  <span className="font-mono text-xs">{emailConfig.smtp_host || '—'}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Status</span>
                  <Badge
                    variant="outline"
                    className={`text-xs ${emailConfig.configured
                      ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                      : 'bg-amber-500/10 text-amber-300 border-amber-500/30'}`}
                  >
                    {emailConfig.configured ? '✓ Skonfigurowane' : '⚠ Nieskonfigurowane'}
                  </Badge>
                </div>
                {emailConfig.note && (
                  <p className="text-xs text-muted-foreground mt-2 p-2 rounded bg-muted/30">{emailConfig.note}</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-amber-300">
                  {lang === 'pl'
                    ? '⚠ Brak odpowiedzi z serwera — spróbuj odświeżyć stronę (Ctrl+Shift+R)'
                    : '⚠ No response from server — try hard refresh (Ctrl+Shift+R)'}
                </p>
                <Button variant="outline" size="sm" onClick={loadData} className="h-7 text-xs">
                  {lang === 'pl' ? 'Odśwież' : 'Refresh'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* T13 2026-05-27 — Konfiguracja agenta (zastępuje forced 7-step wizard) */}
        <Card className="border border-border" data-testid="settings-agent-config-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Bot className="w-4 h-4 text-primary" />
              {lang === 'pl' ? 'Konfiguracja agenta (Adam)' : 'Agent configuration (Adam)'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {lang === 'pl'
                ? 'Ustaw poszczególne kanały komunikacji Adama — zmieniaj gdy chcesz, nie musisz wszystkiego od razu.'
                : 'Configure Adam\'s communication channels — change anytime, no upfront commitment.'}
            </p>

            {/* Quick action — 3 features w grid (email/phone/voice) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => emailGate.openConfig(() => toast.success('Email zapisany'))}
                className="justify-start h-auto py-2 px-3"
              >
                <div className="flex items-center gap-2 w-full">
                  <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                  <div className="flex-1 text-left">
                    <div className="text-xs font-medium">Email Adama</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {emailGate.config?.identity?.email || 'Niezustawione'}
                    </div>
                  </div>
                  {emailGate.needsConfig && (
                    <Badge variant="outline" className="text-[9px] py-0 border-amber-500/30 text-amber-400">SET</Badge>
                  )}
                </div>
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => phoneGate.openConfig(() => toast.success('Telefon zapisany'))}
                className="justify-start h-auto py-2 px-3"
              >
                <div className="flex items-center gap-2 w-full">
                  <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                  <div className="flex-1 text-left">
                    <div className="text-xs font-medium">Telefon Adama</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {phoneGate.config?.identity?.phone || 'Niezustawione'}
                    </div>
                  </div>
                  {phoneGate.needsConfig && (
                    <Badge variant="outline" className="text-[9px] py-0 border-amber-500/30 text-amber-400">SET</Badge>
                  )}
                </div>
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => voiceGate.openConfig(() => toast.success('Głos zapisany'))}
                className="justify-start h-auto py-2 px-3"
              >
                <div className="flex items-center gap-2 w-full">
                  <Mic className="w-3.5 h-3.5 flex-shrink-0" />
                  <div className="flex-1 text-left">
                    <div className="text-xs font-medium">Głos Adama</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {voiceGate.config?.identity?.voice_id || 'onyx (default)'}
                    </div>
                  </div>
                </div>
              </Button>
            </div>

            {/* Power-user link do pełnej konfiguracji */}
            <div className="pt-2 border-t border-border">
              <Link to="/agent-config" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                <SettingsIcon className="w-3 h-3" />
                {lang === 'pl' ? 'Pełna konfiguracja (advanced — imię, bio, role, beliefs)' : 'Full configuration (advanced)'}
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {/* Renderuj modale tutaj — useAgentConfigGate wraca komponent <ConfigModal /> */}
            <emailGate.ConfigModal />
            <phoneGate.ConfigModal />
            <voiceGate.ConfigModal />
          </CardContent>
        </Card>

        {/* T32 2026-05-29 — Telegram Bot pairing */}
        <Card className="border border-border" data-testid="settings-telegram-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Send className="w-4 h-4 text-primary" />
              {lang === 'pl' ? 'Powiadomienia Telegram' : 'Telegram notifications'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {tgStatus?.linked ? (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                    ✓ Połączono
                  </Badge>
                  {tgStatus.telegram_username && (
                    <span className="text-xs text-muted-foreground">
                      @{tgStatus.telegram_username}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Otrzymujesz powiadomienia o nowych zgłoszeniach użytkowników, krytycznych
                  awariach i statusach Twoich klientów.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleTgTest} disabled={tgBusy} className="gap-1.5">
                    <Send className="w-3.5 h-3.5" /> Wyślij test
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleTgUnlink} disabled={tgBusy}
                          className="gap-1.5 text-destructive hover:text-destructive">
                    <Unlink className="w-3.5 h-3.5" /> Odłącz
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Dostaniesz pinga w Telegramie kiedy ktoś zgłosi problem, gdy cron padnie,
                  albo gdy Iza potwierdzi fix. Bot: <code className="text-xs">@maskilpl_bot</code>
                </p>
                <Button onClick={handleTgPair} disabled={tgBusy} size="sm" className="gap-1.5">
                  {tgBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Połącz Telegram
                </Button>
                <p className="text-[10px] text-muted-foreground">
                  Otworzy się nowa karta z Telegramem. Kliknij START — bot zapisze parowanie.
                  Link wygasa po 15 minutach.
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Tutorial / Onboarding */}
        <Card className="border border-border" data-testid="settings-tutorial-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-primary" />
              {lang === 'pl' ? 'Samouczek' : 'Tutorial'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {lang === 'pl'
                ? 'Mission Board prowadzi przez pierwszy pełny cykl przetargu (od wyszukania do wyniku). Resetuj, gdy chcesz przejść samouczek od początku lub pokazać go nowej osobie z zespołu.'
                : 'Mission Board guides you through the first full tender cycle (discovery to outcome). Reset to walk through it from scratch or show a teammate.'}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRestartTutorial}
              disabled={restartingTutorial}
              data-testid="settings-restart-tutorial"
              className="gap-2"
            >
              {restartingTutorial ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              {lang === 'pl' ? 'Pokaż samouczek od nowa' : 'Restart tutorial'}
            </Button>
          </CardContent>
        </Card>

        {/* Data Export */}
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Download className="w-4 h-4 text-primary" />
              {lang === 'pl' ? 'Eksport danych' : 'Data Export'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[{type: 'negotiations', label: lang === 'pl' ? 'Negocjacje' : 'Negotiations'},
                {type: 'contractors', label: lang === 'pl' ? 'Kontrahenci' : 'Contractors'},
                {type: 'reports', label: lang === 'pl' ? 'Raporty' : 'Reports'}].map(({type, label}) => (
                <div key={type} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                  <span className="text-sm">{label}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => handleExport(type, 'json')}>
                      JSON
                    </Button>
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => handleExport(type, 'csv')}>
                      CSV
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
